import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { workoutService } from "../services/workout";
import { Button } from "../components/ui/button";
import {
  Loader2,
  Dumbbell,
  SortDesc,
  Play,
  Edit,
  Clock,
  CalendarX,
  BarChart,
} from "lucide-react";
import { WorkoutCard } from "../components/workout/WorkoutCard";
import { toast } from "sonner";
import { DbWorkout, DbExercise } from "@/types/database";
import {
  Card,
  CardContent,
  CardDescription,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { supabase } from "../integrations/supabase/client";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  format,
  isToday,
  isYesterday,
  differenceInDays,
  differenceInWeeks,
  differenceInMonths,
} from "date-fns";
import { cn } from "@/utils/cn";
import { formatTime } from "@/utils/formatTime";

// Add a type for workout with last used date
type WorkoutWithUsage = DbWorkout & {
  lastUsed?: Date | null;
  timesUsed: number;
};

type SortOption = "name" | "newest" | "oldest" | "least-used";

export default function MyWorkoutsPage() {
  const [workouts, setWorkouts] = useState<WorkoutWithUsage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortBy, setSortBy] = useState<SortOption>("least-used"); // Default to least-used
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchWorkoutsWithUsageData() {
      try {
        setIsLoading(true);

        // Fetch workouts
        const workoutsData = await workoutService.getMyWorkouts();

        // Fetch workout usage data from workout_logs table
        const workoutIds = workoutsData.map((workout) => workout.id);

        // Get workout logs for these workouts
        const { data: workoutLogs, error } = await supabase
          .from("workout_logs")
          .select("workout_id, created_at")
          .in("workout_id", workoutIds)
          .order("created_at", { ascending: false });

        if (error) {
          console.error("Error fetching workout logs:", error);
          throw error;
        }

        // Process the workout logs to get usage data
        const workoutUsageMap = new Map();

        // Initialize with zero usage for all workouts
        workoutIds.forEach((id) => {
          workoutUsageMap.set(id, {
            lastUsed: null,
            timesUsed: 0,
          });
        });

        // Update with actual usage data
        workoutLogs.forEach((log) => {
          const workoutId = log.workout_id;
          const logDate = new Date(log.created_at);

          const currentData = workoutUsageMap.get(workoutId);

          // Update last used date if this is more recent
          if (
            !currentData.lastUsed ||
            (logDate && logDate > currentData.lastUsed)
          ) {
            currentData.lastUsed = logDate;
          }

          // Increment usage count
          currentData.timesUsed += 1;

          workoutUsageMap.set(workoutId, currentData);
        });

        // Combine workout data with usage data
        const workoutsWithUsage = workoutsData.map((workout) => ({
          ...workout,
          lastUsed: workoutUsageMap.get(workout.id)?.lastUsed || null,
          timesUsed: workoutUsageMap.get(workout.id)?.timesUsed || 0,
        }));

        setWorkouts(workoutsWithUsage);
      } catch (error) {
        console.error("Error fetching workouts with usage data:", error);
        toast.error("Failed to load workouts");
      } finally {
        setIsLoading(false);
      }
    }

    fetchWorkoutsWithUsageData();
  }, []);

  // Format the last used date in a user-friendly way
  const formatLastUsed = (lastUsed: Date | null) => {
    if (!lastUsed) return "Never used";

    if (isToday(lastUsed)) {
      return `Today`;
    }

    if (isYesterday(lastUsed)) {
      return `Yesterday`;
    }

    const daysDiff = differenceInDays(new Date(), lastUsed);

    if (daysDiff < 7) {
      return `${daysDiff} days ago`;
    }

    const weeksDiff = differenceInWeeks(new Date(), lastUsed);
    if (weeksDiff < 4) {
      return weeksDiff === 1 ? "1 week ago" : `${weeksDiff} weeks ago`;
    }

    const monthsDiff = differenceInMonths(new Date(), lastUsed);
    if (monthsDiff < 12) {
      return monthsDiff === 1 ? "1 month ago" : `${monthsDiff} months ago`;
    }

    return format(lastUsed, "MMM d, yyyy");
  };

  // Get workouts that have never been used
  const neverUsedWorkouts = workouts.filter((w) => !w.lastUsed);

  // Sort workouts based on selected option
  const sortedWorkouts = [...workouts].sort((a, b) => {
    switch (sortBy) {
      case "name":
        return a.name.localeCompare(b.name);
      case "newest":
        return (
          (b.lastUsed || new Date(0)).getTime() -
          (a.lastUsed || new Date(0)).getTime()
        );
      case "oldest":
        return (
          (a.lastUsed || new Date(0)).getTime() -
          (b.lastUsed || new Date(0)).getTime()
        );
      case "least-used":
        // Never used workouts come first
        if (!a.lastUsed && b.lastUsed) return -1;
        if (a.lastUsed && !b.lastUsed) return 1;
        if (!a.lastUsed && !b.lastUsed) return 0;
        // Then sort by date (oldest first)
        return (
          (a.lastUsed || new Date(0)).getTime() -
          (b.lastUsed || new Date(0)).getTime()
        );
      default:
        return 0;
    }
  });

  // Determine if we should use list view (for least-used) or grid view
  const isListView = sortBy === "least-used";

  // Helper function to format exercise details
  const formatExerciseDetails = (exercise: DbExercise) => {
    if (exercise.structure.type === "timed") {
      return `${exercise.structure.sets} × ${formatTime(
        exercise.structure.duration
      )}`;
    } else if (exercise.structure.type === "sets") {
      return `${exercise.structure.sets} ${
        exercise.structure.sets === 1 ? "set" : "sets"
      } × ${exercise.structure.reps} reps`;
    } else if (exercise.structure.type === "cadence") {
      return `CaDeNcE`;
    } else if (exercise.structure.type === "interval") {
      return `Interval`;
    } else {
      throw new Error(
        `Unknown exercise type: ${
          (exercise.structure as { type: string }).type
        }`
      );
    }
  };

  return (
    <div className="space-y-6">
      {/* Workouts Section */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : workouts.length === 0 ? (
        <Card className="border border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-8">
            <div className="bg-primary/10 p-3 rounded-full mb-4">
              <Dumbbell className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-lg mb-2">No Workouts Yet</CardTitle>
            <CardDescription className="text-center mb-4 max-w-md">
              Create your first workout to start tracking your fitness journey.
            </CardDescription>
            <Button onClick={() => navigate("/workout/edit/new")}>
              Create Your First Workout
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Filters and Sorting */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-medium">My Workouts</h2>
              <Badge variant="outline" className="ml-2">
                {workouts.length}
              </Badge>
              {neverUsedWorkouts.length > 0 && (
                <Badge
                  variant="outline"
                  className="bg-primary/10 border-primary/20"
                >
                  {neverUsedWorkouts.length} new
                </Badge>
              )}
            </div>

            <div className="flex items-center gap-2">
              <SortDesc className="h-4 w-4 text-muted-foreground" />
              <Select
                value={sortBy}
                onValueChange={(value) => setSortBy(value as SortOption)}
              >
                <SelectTrigger className="w-[180px] h-9">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="least-used">
                    Least Recently Used
                  </SelectItem>
                  <SelectItem value="newest">Newest First</SelectItem>
                  <SelectItem value="oldest">Oldest First</SelectItem>
                  <SelectItem value="name">Name (A-Z)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {isListView ? (
            // Enhanced List View for Least Recently Used
            <div className="space-y-3">
              {sortedWorkouts.map((workout) => (
                <Card
                  key={workout.id}
                  className={cn(
                    "overflow-hidden hover:shadow-sm transition-shadow",
                    !workout.lastUsed && "ring-1 ring-primary/30"
                  )}
                >
                  <div className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                      {/* Workout Info */}
                      <div className="flex-grow">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium">{workout.name}</h3>
                          {!workout.lastUsed && (
                            <Badge className="bg-primary text-primary-foreground">
                              New
                            </Badge>
                          )}
                        </div>

                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground mb-3">
                          <div className="flex items-center">
                            <BarChart className="h-3.5 w-3.5 mr-1" />
                            <span>
                              {workout.exercises?.length || 0} exercises
                            </span>
                          </div>

                          <div className="flex items-center">
                            {!workout.lastUsed ? (
                              <span className="flex items-center">
                                <CalendarX className="h-3.5 w-3.5 mr-1" />
                                Never used
                              </span>
                            ) : (
                              <span className="flex items-center">
                                <Clock className="h-3.5 w-3.5 mr-1" />
                                Last: {formatLastUsed(workout.lastUsed)}
                              </span>
                            )}
                          </div>

                          {workout.timesUsed > 0 && (
                            <span>
                              Used {workout.timesUsed}{" "}
                              {workout.timesUsed === 1 ? "time" : "times"}
                            </span>
                          )}
                        </div>

                        {/* Exercise Preview - Vertical List */}
                        {workout.exercises && workout.exercises.length > 0 && (
                          <div className="border-t pt-2 mt-2">
                            <div className="space-y-1">
                              {workout.exercises
                                .slice(0, 5)
                                .map((exercise, index) => (
                                  <div
                                    key={index}
                                    className="flex justify-between text-sm py-1"
                                  >
                                    <div className="flex items-center">
                                      <div
                                        className={cn(
                                          "w-1.5 h-1.5 rounded-full mr-2",
                                          exercise.structure.type === "sets"
                                            ? "bg-red-500"
                                            : exercise.structure.type ===
                                              "timed"
                                            ? "bg-blue-500"
                                            : "bg-purple-500"
                                        )}
                                      />
                                      <span className="font-medium">
                                        {exercise.name}
                                      </span>
                                    </div>
                                    <span className="text-muted-foreground ml-2 text-right">
                                      {formatExerciseDetails(exercise)}
                                    </span>
                                  </div>
                                ))}
                              {workout.exercises.length > 5 && (
                                <div className="text-sm text-muted-foreground py-1">
                                  +{workout.exercises.length - 5} more exercises
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 self-end sm:self-start sm:mt-1 sm:ml-2 shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            navigate(`/workout/edit/${workout.id}`)
                          }
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          onClick={() =>
                            navigate(`/workout/start/${workout.id}`)
                          }
                        >
                          <Play className="h-4 w-4 mr-1" />
                          Start
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            // Grid View for other sort options
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sortedWorkouts.map((workout) => (
                <WorkoutCard
                  key={workout.id}
                  workout={workout}
                  onStart={() => navigate(`/workout/start/${workout.id}`)}
                  onEdit={() => navigate(`/workout/edit/${workout.id}`)}
                  lastUsed={workout.lastUsed ?? null}
                  timesUsed={workout.timesUsed}
                  highlight={
                    !workout.lastUsed && sortBy === "oldest" ? true : false
                  }
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
